(() => {
  const COLLECTION_SCROLL_RESET_KEY = 'aureme:collection-scroll-reset';
  const COLLECTION_SCROLL_RESET_TTL_MS = 10000;
  const COLLECTION_SCROLL_RESET_DELAYS_MS = [0, 50, 150, 300, 600, 1000, 1500];
  const COLLECTION_SCROLL_RESET_CLEANUP_DELAY_MS =
    COLLECTION_SCROLL_RESET_DELAYS_MS[COLLECTION_SCROLL_RESET_DELAYS_MS.length - 1] + 100;
  /*
   * ロケール接頭辞を許容しつつ、`/collections/<handle>` 単体だけを対象にする。
   * 配下の商品パスでは scroll reset を発火させない。
   */
  const COLLECTION_PATH_PATTERN = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i;
  // URL 正規化は theme.liquid で先行読み込みする aureme-url.js に集約する。
  const auremeUrl = window.AuremeUrl;
  if (!auremeUrl) return;
  const scrollRestoreUtils = window.AuremeScrollRestoreUtils;
  if (
    typeof scrollRestoreUtils?.consumePathnameTimestampIntent !== 'function' ||
    typeof scrollRestoreUtils?.isFreshPathnameTimestampIntent !== 'function' ||
    !scrollRestoreUtils?.safeSessionStorage ||
    typeof scrollRestoreUtils.safeSessionStorage.getItem !== 'function' ||
    typeof scrollRestoreUtils.safeSessionStorage.setItem !== 'function' ||
    typeof scrollRestoreUtils.safeSessionStorage.removeItem !== 'function' ||
    typeof scrollRestoreUtils.bindScrollRestoreCancelEvents !== 'function' ||
    typeof scrollRestoreUtils.scheduleRepeatedScrollAction !== 'function' ||
    typeof scrollRestoreUtils.temporarilyUseManualScrollRestoration !== 'function'
  ) {
    console.error('[aureme-collection-scroll-reset] scroll restore utils are not loaded.');
    return;
  }
  const {
    bindScrollRestoreCancelEvents,
    consumePathnameTimestampIntent,
    isFreshPathnameTimestampIntent,
    safeSessionStorage,
    scheduleRepeatedScrollAction,
    temporarilyUseManualScrollRestoration,
  } = scrollRestoreUtils;
  const SCROLL_RESET_CANCEL_EVENTS = ['pointerdown', 'mousedown', 'wheel', 'touchmove'];
  const SCROLL_KEYS = new Set(['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' ']);

  /**
   * @typedef {object} CollectionScrollReset
   * @property {string} pathname - 初期表示を先頭に戻す Collection pathname
   * @property {number} timestamp - 記録時刻
   */

  /**
   * Collection URL かどうかを判定する。
   * @param {string} href - 判定対象の URL
   * @returns {URL | null}
   */
  function getSameOriginCollectionUrl(href) {
    const collectionUrl = auremeUrl.getSameOriginUrl(href);
    if (!collectionUrl) return null;
    if (!COLLECTION_PATH_PATTERN.test(collectionUrl.pathname)) return null;
    return collectionUrl;
  }

  /**
   * Collection への通常クリック遷移だけを抽出する。
   * @param {MouseEvent} event - クリックイベント
   * @returns {URL | null}
   */
  function getCollectionNavigationUrl(event) {
    if (event.defaultPrevented || event.button !== 0) return null;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    if (!(event.target instanceof Element)) return null;

    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return null;
    if (link.target && link.target !== '_self') return null;
    if (link.hasAttribute('download')) return null;

    const collectionUrl = getSameOriginCollectionUrl(link.href);
    if (!collectionUrl) return null;
    if (collectionUrl.hash) return null;

    return collectionUrl;
  }

  /**
   * Collection 初期表示リセット情報を読み出し、保存値を消費する。
   * @returns {CollectionScrollReset | null}
   */
  function consumeCollectionScrollReset() {
    return consumePathnameTimestampIntent(COLLECTION_SCROLL_RESET_KEY, {
      normalizePathname: auremeUrl.normalizePathname,
    });
  }

  /**
   * 商品一覧リンクの通常遷移では、次回の Collection 初期表示を先頭に固定する。
   */
  function bindCollectionScrollResetLinkClick() {
    document.addEventListener(
      'click',
      (event) => {
        const collectionUrl = getCollectionNavigationUrl(event);
        if (!collectionUrl) return;

        const targetPathname = auremeUrl.normalizePathname(collectionUrl.pathname);
        const currentCollectionUrl = getSameOriginCollectionUrl(window.location.href);
        if (
          currentCollectionUrl &&
          auremeUrl.normalizePathname(currentCollectionUrl.pathname) === targetPathname
        ) {
          return;
        }

        safeSessionStorage.setItem(
          COLLECTION_SCROLL_RESET_KEY,
          JSON.stringify({
            pathname: targetPathname,
            timestamp: Date.now(),
          }),
        );
      },
      true,
    );
  }

  /**
   * 初期描画中に先頭へ戻す処理を予約する。
   * @param {() => void} scrollToTop - 先頭へ戻す処理
   * @returns {() => void}
   */
  function scheduleScrollTopRetries(scrollToTop) {
    return scheduleRepeatedScrollAction(scrollToTop, {
      delaysMs: COLLECTION_SCROLL_RESET_DELAYS_MS,
      includeLoad: true,
    });
  }

  /**
   * ユーザー操作とページ離脱時のキャンセル処理を登録する。
   * @param {object} handlers - イベント処理群
   * @param {() => void} handlers.cancelScrollReset - ユーザー操作時のキャンセル処理
   * @param {() => void} handlers.cleanup - ページ離脱またはキャンセル時のクリーンアップ処理
   * @returns {() => void}
   */
  function bindScrollResetLifecycleHandlers({ cancelScrollReset, cleanup }) {
    const unbindCancelEvents = bindScrollRestoreCancelEvents(cancelScrollReset, {
      events: SCROLL_RESET_CANCEL_EVENTS,
      isCancelKey: (event) => SCROLL_KEYS.has(event.key),
    });
    window.addEventListener('pagehide', cleanup, { once: true });

    return () => {
      unbindCancelEvents();
      window.removeEventListener('pagehide', cleanup);
    };
  }

  /**
   * 遅延したネイティブのスクロール復元に上書きされないよう、初期描画中だけ先頭へ戻す。
   */
  function forceCollectionScrollTop() {
    let isCleanedUp = false;
    let cleanupTimer = 0;
    let cancelScrollTopRetries = () => {};
    let unbindScrollResetLifecycleHandlers = () => {};
    const restoreScrollRestoration = temporarilyUseManualScrollRestoration();

    const scrollToTop = () => {
      if (isCleanedUp) return;
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const cleanup = () => {
      if (isCleanedUp) return;
      isCleanedUp = true;

      unbindScrollResetLifecycleHandlers();
      cancelScrollTopRetries();
      window.clearTimeout(cleanupTimer);
      restoreScrollRestoration();
    };

    const cancelScrollReset = cleanup;

    unbindScrollResetLifecycleHandlers = bindScrollResetLifecycleHandlers({
      cancelScrollReset,
      cleanup,
    });
    cancelScrollTopRetries = scheduleScrollTopRetries(scrollToTop);
    cleanupTimer = window.setTimeout(cleanup, COLLECTION_SCROLL_RESET_CLEANUP_DELAY_MS);
  }

  /**
   * Collection 初期表示リセットを現在のページに適用するか判定する。
   * @param {CollectionScrollReset} reset - 検証済みのリセット情報
   * @param {URL} collectionUrl - 現在の Collection URL
   * @returns {boolean}
   */
  function shouldApplyCollectionScrollReset(reset, collectionUrl) {
    return isFreshPathnameTimestampIntent(reset, {
      currentPathname: auremeUrl.normalizePathname(collectionUrl.pathname),
      ttlMs: COLLECTION_SCROLL_RESET_TTL_MS,
    });
  }

  /**
   * 直前ページで記録した Collection 初期表示リセットを必要に応じて実行する。
   */
  function applyPendingCollectionScrollReset() {
    const collectionUrl = getSameOriginCollectionUrl(window.location.href);
    if (!collectionUrl) {
      safeSessionStorage.removeItem(COLLECTION_SCROLL_RESET_KEY);
      return;
    }

    const reset = consumeCollectionScrollReset();
    if (!reset) return;

    if (shouldApplyCollectionScrollReset(reset, collectionUrl)) {
      forceCollectionScrollTop();
    }
  }

  /**
   * Collection 遷移時のスクロール位置制御を初期化する。
   */
  function initCollectionScrollReset() {
    bindCollectionScrollResetLinkClick();
    applyPendingCollectionScrollReset();
  }

  initCollectionScrollReset();
})();
